/**
 * SignaturePad — szabadkézi canvas-aláírás primitív (contract-SIGN FE-slice).
 *
 * Tiszta, önálló komponens: NINCS i18n-függés (a label-eket prop-on kapja, HU default),
 * így nem esik az RTL stabil-`t` re-render-csapdába, és bárhol újrahasznosítható.
 * A pult-tablet flow aláíró-felülete: az ujjal/tollal rajzolt aláírást `png` dataURL-ként
 * adja vissza + `strokeCount`/`durationMs` audit-jelekkel (a `@kgc/esign` `SubmitSignatureSchema`-hoz).
 *
 * jsdom-safe: a `getContext('2d')` / `toDataURL()` hiányát (teszt-környezet) elnyeli.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface SignatureResult {
  /** A kirajzolt aláírás `png` dataURL-ként (üres mezőnél `''`). */
  payload: string;
  format: 'png';
  /** Húzások (stroke-ok) száma — audit-jel. */
  strokeCount: number;
  /** Az első ponttól az utolsó húzásig eltelt idő ms-ben — audit-jel. */
  durationMs: number;
  /** `true`, ha még semmit nem rajzoltak. */
  isEmpty: boolean;
}

export interface SignaturePadProps {
  onChange?: (result: SignatureResult) => void;
  width?: number;
  height?: number;
  disabled?: boolean;
  clearLabel?: string;
  hintLabel?: string;
}

export function SignaturePad({
  onChange,
  width = 600,
  height = 200,
  disabled = false,
  clearLabel = 'Törlés',
  hintLabel = 'Írja alá az ujjával vagy tollal a mezőben',
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const startTs = useRef<number | null>(null);
  const strokes = useRef(0);
  const [empty, setEmpty] = useState(true);

  const ctx = useCallback(() => {
    const c = canvasRef.current;
    try {
      return c ? c.getContext('2d') : null;
    } catch {
      return null;
    }
  }, []);

  const emit = useCallback(() => {
    const c = canvasRef.current;
    let payload = '';
    try {
      payload = c ? c.toDataURL('image/png') : '';
    } catch {
      payload = '';
    }
    onChange?.({
      payload: empty ? '' : payload,
      format: 'png',
      strokeCount: strokes.current,
      durationMs: startTs.current ? Math.max(0, Date.now() - startTs.current) : 0,
      isEmpty: empty,
    });
  }, [onChange, empty]);

  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current;
    if (!c) return { x: 0, y: 0 };
    const r = c.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    drawing.current = true;
    last.current = pos(e);
    if (startTs.current === null) startTs.current = Date.now();
    try {
      canvasRef.current?.setPointerCapture(e.pointerId);
    } catch {
      /* jsdom / nem támogatott pointer-capture */
    }
  };

  const onMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || disabled) return;
    const g = ctx();
    const p = pos(e);
    if (g && last.current) {
      g.strokeStyle = '#0f172a';
      g.lineWidth = 2.2;
      g.lineCap = 'round';
      g.lineJoin = 'round';
      g.beginPath();
      g.moveTo(last.current.x, last.current.y);
      g.lineTo(p.x, p.y);
      g.stroke();
    }
    last.current = p;
    if (empty) setEmpty(false);
  };

  const onUp = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    strokes.current += 1;
    emit();
  };

  const clear = () => {
    const g = ctx();
    const c = canvasRef.current;
    if (g && c) g.clearRect(0, 0, c.width, c.height);
    drawing.current = false;
    last.current = null;
    startTs.current = null;
    strokes.current = 0;
    setEmpty(true);
    onChange?.({ payload: '', format: 'png', strokeCount: 0, durationMs: 0, isEmpty: true });
  };

  // a setEmpty(false) utáni friss `empty`-vel újra-emittál, hogy a payload ne üresként menjen
  useEffect(() => {
    if (!empty) emit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empty]);

  return (
    <div className="flex flex-col gap-2">
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        role="img"
        aria-label="signature-pad"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
        className={`touch-none rounded-md border border-dashed bg-white ${
          disabled ? 'opacity-50' : 'cursor-crosshair'
        }`}
        style={{ width: '100%', maxWidth: width, height }}
      />
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{hintLabel}</span>
        <button
          type="button"
          onClick={clear}
          disabled={disabled}
          className="rounded border px-2 py-1 hover:bg-gray-50 disabled:opacity-50"
        >
          {clearLabel}
        </button>
      </div>
    </div>
  );
}
